import { cloneDeep } from 'lodash';
import {
  columnCreatedOn,
  columnDescription,
  columnName,
  columnSize,
  columnTransferable,
  columnType,
  columnViewing,
  orderByColumns,
} from '@/constants/fields';
import parseDate from '@/utils/dateHelper';
import { isFileTooLargeToDownload } from '@/utils/fileHelper';
import { hasMinimumLength } from '@/utils/resultsHelper';
import { ATTACHMENT_TRANSFER, ATTACHMENT_TYPE, ATTACHMENT_VIEW } from './constants';

export const resolveCurrentPath = (
  segments,
) => (segments.length > 0 ? `${segments.at(-1)?.id}/` : '/');

export const getAttachmentType = (attachment, defaultValue = ATTACHMENT_TYPE.attachment) => {
  const fileType = attachment?.metadata?.dataset_file_type;
  return Object.values(ATTACHMENT_TYPE).find(({ name }) => name === fileType) || defaultValue;
};

const matchAttachmentType = (attachment, { name }) => getAttachmentType(attachment).name === name;

const matchType = (attachment, { byType }) => (
  Object.keys(byType).length === 0 || byType[getAttachmentType(attachment).name]
);

const matchFilter = (attachment, { byTextInput }) => (
  !hasMinimumLength(byTextInput) || attachment.name.toLowerCase().includes(byTextInput.toLowerCase())
);

const matchHidden = (attachment, withHidden) => (
  withHidden || attachment.metadata?.viewing !== ATTACHMENT_VIEW.hidden.name
);

const matchFolders = (attachment, folders) => {
  if (folders.currentPathSegments.length === 0) {
    return true;
  }

  const folderPath = resolveCurrentPath(folders.currentPathSegments);

  return attachment.id.startsWith(folderPath);
};

export const filterAttachments = (items, filters, folders, withHidden = true) => (items.reduce((acc, item) => {
  if (matchType(item, filters)
    && (folders ? matchFolders(item, folders) : true)
    && matchFilter(item, filters)
    && matchHidden(item, withHidden)) {
    acc.push(item);
  }
  return acc;
}, [])
);

const simpleCompare = (a, b, ascending) => (ascending ? a - b : b - a);

const stringCompare = (a, b, ascending) => (
  ascending ? a.toLowerCase().localeCompare(b.toLowerCase()) : b.toLowerCase().localeCompare(a.toLowerCase())
);

const compareCreatedAt = (a, b, ascending) => (
  simpleCompare(parseDate(a.created_at), parseDate(b.created_at), ascending)
);

const compareName = (a, b, ascending) => stringCompare(a.name, b.name, ascending);

const compareSize = (a, b, ascending) => simpleCompare(a.size, b.size, ascending);

const compareType = (a, b, ascending) => stringCompare(getAttachmentType(a).name, getAttachmentType(b).name, ascending);

const compareViewing = (a, b, ascending) => (simpleCompare(
  ATTACHMENT_VIEW[a.metadata.viewing]?.order || 0,
  ATTACHMENT_VIEW[b.metadata.viewing]?.order || 0,
  ascending,
));

const compareTransferable = (a, b, ascending) => (simpleCompare(
  ATTACHMENT_TRANSFER[a.metadata.transferable]?.order || 0,
  ATTACHMENT_TRANSFER[b.metadata.transferable]?.order || 0,
  ascending,
));

const compareDescription = (a, b, ascending) => stringCompare(
  a.metadata.description || '',
  b.metadata.description || '',
  ascending,
);

export const sortAttachments = (items, orderBy) => {
  const ascending = orderBy.length === 0 ? null : orderBy[0].direction === 'asc';
  const comparer = {
    [orderByColumns[columnCreatedOn]]: compareCreatedAt,
    [columnName]: compareName,
    [columnSize]: compareSize,
    [columnTransferable]: compareTransferable,
    [columnType]: compareType,
    [columnViewing]: compareViewing,
    [columnDescription]: compareDescription,
  }[orderBy.length === 0 ? null : orderBy[0].column];

  if (!comparer) {
    return items;
  }

  const folders = items
    .filter((item) => matchAttachmentType(item, ATTACHMENT_TYPE.folder))
    .sort((a, b) => comparer(a, b, ascending));
  const files = items
    .filter((item) => !matchAttachmentType(item, ATTACHMENT_TYPE.folder))
    .sort((a, b) => comparer(a, b, ascending));

  return ascending ? [...folders, ...files] : [...files, ...folders];
};

export const attachmentIcon = (attachment) => {
  const { name, icon } = getAttachmentType(attachment);
  return { iconName: icon, iconTitle: `icon.${name}` };
};

export const downLoadableAttachmentIcon = (attachment, isDownloading) => {
  const icon = attachmentIcon(attachment);
  icon.exceedsDownloadSize = isFileTooLargeToDownload(attachment.size);
  icon.warning = (matchAttachmentType(attachment, ATTACHMENT_TYPE.datafile)
    || matchAttachmentType(attachment, ATTACHMENT_TYPE.folder))
    && attachment.metadata.viewing === ATTACHMENT_VIEW.downloadable.name;

  if (icon.exceedsDownloadSize) {
    icon.color = 'red';
    icon.iconName = 'dont';
    icon.iconTitle = 'icon.download.unable';
  }

  if (isDownloading) {
    icon.iconTitle = 'icon.download.preparing';
  }

  return icon;
};

export const attachmentViewingOptions = (attachment) => {
  if (matchAttachmentType(attachment, ATTACHMENT_TYPE.attachment)) {
    return ATTACHMENT_VIEW;
  }

  const options = cloneDeep(ATTACHMENT_VIEW);
  options.downloadable.className = 'warning';
  return options;
};

export const confirmDownloadable = (attachment, value) => (
  value === ATTACHMENT_VIEW.downloadable.name && (
    matchAttachmentType(attachment, ATTACHMENT_TYPE.datafile)
    || matchAttachmentType(attachment, ATTACHMENT_TYPE.folder))
);

const filteredOptions = (options, value) => (
  Object.values(options).filter(({ name }) => value !== name).sort((a, b) => a.order - b.order)
);

export const filterAttachmentTransferable = ({ transferable }) => filteredOptions(ATTACHMENT_TRANSFER, transferable);

export const filterAttachmentViewing = ({ viewing }) => filteredOptions(ATTACHMENT_VIEW, viewing);

export const computeBreadcrumbIsRoot = (breadcrumb) => breadcrumb.currentPathSegments.length === 0;

export const computeBreadcrumbSegments = (initialSegment, breadcrumb) => {
  const segments = [initialSegment, ...breadcrumb.currentPathSegments]
    .filter(Boolean)
    .map((segment, index) => ({
      ...segment,
      segmentLocation: breadcrumb.currentPathSegments.slice(
        0,
        index + (initialSegment ? 0 : 1),
      ),
    }));

  if (segments.length > 4) {
    const firstSegments = segments.slice(0, 2);
    const lastSegments = segments.slice(segments.length - 2);

    return { segments: [...firstSegments, { ellipsis: true }, ...lastSegments], isRoot: false };
  }

  return { segments, isRoot: computeBreadcrumbIsRoot(breadcrumb) };
};

export default matchAttachmentType;
